import { SSLogoIcon as SSLogo } from "../components/SSLogo";
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Zap, Mail, Lock, User, Phone, Globe, Shield, CheckCircle } from 'lucide-react';

export default function RegisterPage() {
  const { t, isRTL, toggleLanguage, lang } = useLanguage();
  const { register, verifyEmail, verifyPhone, verificationCodes } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error(isRTL ? 'الرجاء ملء جميع الحقول المطلوبة' : 'Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const data = await register(name, email, password, phone);
      toast.success(isRTL ? 'تم إنشاء الحساب بنجاح!' : 'Account created successfully!');
      setVerifyDialog(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    setVerifying(true);
    try {
      await verifyEmail(emailCode);
      setEmailVerified(true);
      toast.success(isRTL ? 'تم تأكيد البريد الإلكتروني!' : 'Email verified!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleVerifyPhone = async () => {
    setVerifying(true);
    try {
      await verifyPhone(phoneCode);
      setPhoneVerified(true);
      toast.success(isRTL ? 'تم تأكيد رقم الهاتف!' : 'Phone verified!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setVerifying(false);
    }
  };

  const handleContinue = () => {
    setVerifyDialog(false);
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex">
      {/* Left / Right side illustration */}
      <div className={`hidden lg:flex lg:w-1/2 hero-gradient items-center justify-center p-12 ${isRTL ? 'order-2' : 'order-1'}`}>
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <SSLogo size={36} />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">SHOP & SHIP</h1>
              <p className="text-sm text-muted-foreground">{isRTL ? 'منصة التجارة' : 'Sell & Earn'}</p>
            </div>
          </div>
          <h2 className="font-heading text-4xl font-bold mb-4">
            {isRTL ? 'انضم إلينا وابدأ الكسب' : 'Join us and start earning'}
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {isRTL 
              ? 'سجل حسابك الآن وابدأ في كسب العمولات من متاجر متعددة'
              : 'Register now and start earning commissions from multiple marketplaces'
            }
          </p>
          <div className="mt-8 space-y-3">
            {[
              { icon: Shield, text: isRTL ? 'حساب آمن وموثق' : 'Secure & verified account' },
              { icon: CheckCircle, text: isRTL ? 'بدء الكسب فوراً' : 'Start earning immediately' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                <item.icon className="w-4 h-4 text-primary" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className={`flex-1 flex items-center justify-center p-6 ${isRTL ? 'order-1' : 'order-2'}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="flex justify-end mb-6">
            <Button variant="ghost" size="sm" onClick={toggleLanguage} className="gap-2">
              <Globe className="w-4 h-4" />
              {lang === 'ar' ? 'English' : 'عربي'}
            </Button>
          </div>

          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <SSLogo size={32} />
            </div>
            <h1 className="font-heading text-xl font-bold">SHOP & SHIP</h1>
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-2xl font-heading">{t('register')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t('name')} *</label>
                  <div className="relative">
                    <User className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('namePlaceholder')}
                      className={isRTL ? 'pr-10' : 'pl-10'}
                      data-testid="auth-name-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t('email')} *</label>
                  <div className="relative">
                    <Mail className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('emailPlaceholder')}
                      className={isRTL ? 'pr-10' : 'pl-10'}
                      dir="ltr"
                      data-testid="auth-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t('password')} *</label>
                  <div className="relative">
                    <Lock className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('passwordPlaceholder')}
                      className={isRTL ? 'pr-10' : 'pl-10'}
                      data-testid="auth-password-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t('phone')}</label>
                  <div className="relative">
                    <Phone className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                    <Input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t('phonePlaceholder')}
                      className={isRTL ? 'pr-10' : 'pl-10'}
                      dir="ltr"
                      data-testid="auth-phone-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full gap-2" 
                  disabled={loading}
                  data-testid="auth-submit-button"
                >
                  <UserPlus className="w-4 h-4" />
                  {loading ? '...' : t('register')}
                </Button>
              </form>

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">{isRTL ? 'أو' : 'OR'}</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Google Register - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <Button variant="outline" className="w-full gap-3 h-11" onClick={() => {
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }} data-testid="google-register-btn">
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {isRTL ? 'التسجيل بـ Google' : 'Sign up with Google'}
              </Button>

              <p className="text-sm text-center text-muted-foreground mt-4">
                {t('alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  {t('login')}
                </Link>
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Verification Dialog */}
      <Dialog open={verifyDialog} onOpenChange={setVerifyDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              {isRTL ? 'تحقق من حسابك' : 'Verify Your Account'}
            </DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'أدخل رموز التحقق المرسلة إليك'
                : 'Enter the verification codes sent to you'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Show codes hint for testing */}
            {verificationCodes && (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-xs text-muted-foreground mb-1">
                  {isRTL ? 'رموز التحقق (للاختبار):' : 'Verification codes (for testing):'}
                </p>
                <p className="font-mono text-sm">
                  {isRTL ? 'البريد' : 'Email'}: {verificationCodes.email_code} | {isRTL ? 'الهاتف' : 'Phone'}: {verificationCodes.phone_code}
                </p>
              </div>
            )}

            {/* Email Verification */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">{t('verifyEmail')}</label>
                {emailVerified && <CheckCircle className="w-4 h-4 text-[hsl(var(--success))]" />}
              </div>
              <div className="flex gap-2">
                <Input
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value)}
                  placeholder={t('verificationCode')}
                  className="font-mono"
                  dir="ltr"
                  disabled={emailVerified}
                  data-testid="otp-email-input"
                />
                <Button 
                  onClick={handleVerifyEmail} 
                  disabled={emailVerified || verifying || !emailCode}
                  size="sm"
                >
                  {emailVerified ? <CheckCircle className="w-4 h-4" /> : t('verify')}
                </Button>
              </div>
            </div>

            {/* Phone Verification */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">{t('verifyPhone')}</label>
                {phoneVerified && <CheckCircle className="w-4 h-4 text-[hsl(var(--success))]" />}
              </div>
              <div className="flex gap-2">
                <Input
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value)}
                  placeholder={t('verificationCode')}
                  className="font-mono"
                  dir="ltr"
                  disabled={phoneVerified}
                  data-testid="otp-phone-input"
                />
                <Button 
                  onClick={handleVerifyPhone} 
                  disabled={phoneVerified || verifying || !phoneCode}
                  size="sm"
                >
                  {phoneVerified ? <CheckCircle className="w-4 h-4" /> : t('verify')}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleContinue} className="w-full gap-2">
              {isRTL ? 'متابعة إلى لوحة التحكم' : 'Continue to Dashboard'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
